import classNames from 'classnames';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import i18next from 'i18next';
import { Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { account } from '@redux/account';
import { useMenu } from 'hooks/useMenu';
import { MenuItem, MenuPanel } from 'components/Menu/MenuPanel';
import { useDispatch } from 'utils/useDispatch';

/*
 * The language picker on the auth screens. It was the last consumer of components/Dropdown
 * (an rc-dropdown/rc-menu wrapper with its own popover chrome, positioning and animation),
 * so it now uses the same useMenu + MenuPanel pair as the playlist chips and device rows —
 * one popover implementation in the app instead of two, and rc-* drops out of the bundle.
 */
const LANGUAGES = [
  { code: 'en', title: 'English' },
  { code: 'ru', title: 'Русский' },
  { code: 'uk', title: 'Українська' },
  { code: 'de', title: 'Deutsch' },
  { code: 'es', title: 'Español' },
  { code: 'fr', title: 'Français' },
  { code: 'it', title: 'Italiano' },
  { code: 'nl', title: 'Nederlands' },
  { code: 'tr', title: 'Türkçe' },
  { code: 'pl', title: 'Polski' },
  { code: 'pt', title: 'Português' },
];

export const LanguageDropdown = ({ className }) => {
  const { t } = useTranslation();
  const language = useSelector(account.selectors.language);
  const setLanguage = useDispatch(account.actions.setLanguage);
  const menu = useMenu({ align: 'right', width: 200 });

  const handleLangChange = useCallback((lng) => {
    setLanguage(lng);
    i18next.changeLanguage(lng);
    menu.close();
  }, [setLanguage, menu]);

  return (
    // No `relative` here: useMenu measures the trigger with getBoundingClientRect and
    // MenuPanel renders the panel `fixed` in a portal, so no positioned ancestor is needed —
    // and adding one fought the caller's own `absolute` (the auth header pins this picker to
    // the right of the title, and a `relative` here silently won the position cascade).
    <div ref={menu.triggerRef} className={classNames('inline-flex', className)}>
      <button
        type="button"
        aria-label={t('settings.language')}
        aria-expanded={menu.isOpen}
        onClick={menu.toggle}
        className="group inline-flex min-h-[44px] items-center gap-1 rounded-row px-3 py-2 uppercase transition-colors duration-150 hover:bg-ui-overlay hover:text-ui focus:outline-none focus-visible:ring-2 focus-visible:ring-ui-accent-ring"
      >
        {language}
        <ChevronDown size={13} className={classNames('transition-transform duration-200', menu.isOpen && 'rotate-180')} />
      </button>

      <MenuPanel menu={menu} className="max-h-[60dvh]">
        {LANGUAGES.map(({ code, title }) => (
          <MenuItem
            key={code}
            onClick={() => handleLangChange(code)}
            className={code === language ? 'text-ui-accent' : undefined}
          >
            <span className="flex-1">{title}</span>
            {code === language && <Check size={14} className="flex-none" />}
          </MenuItem>
        ))}
      </MenuPanel>
    </div>
  );
};
